import React from 'react';
import { Layout, Menu, Space, Button, Tag } from 'antd';
import {
  DashboardOutlined, 
  UserOutlined,
  TeamOutlined,
  FileTextOutlined,
  SettingOutlined,
  LogoutOutlined,
  WalletOutlined,
  FolderOpenOutlined
} from '@ant-design/icons';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import RolePermissions from './pages/RolePermissions';
import PublicLeads from './pages/crm/PublicLeads';
import MyLeads from './pages/Leads';
import FollowUpRecords from './pages/crm/FollowUpRecords';
import Interviews from './pages/Interviews';
import Employees from './pages/Employees';
import Onboarding from './pages/Onboarding';
import Settings from './pages/Settings';
import SystemUpdate from './pages/SystemUpdate';
import Login from './pages/Login';
import CallPage from './pages/mobile/CallPage';
import ExpensesPage from './pages/mobile/ExpensesPage';
import CategoriesPage from './pages/mobile/CategoriesPage';
import CourseManagement from './pages/CourseManagement';
import VideoManagement from './pages/VideoManagement';
import LearningRecords from './pages/LearningRecords';
import LearningPage from './pages/mobile/LearningPage';
import Resignations from './pages/Resignations';
import TestLogin from './pages/TestLogin';
import EmployeesTasksAll from './pages/EmployeesTasksAll';
import SalaryManagement from './pages/SalaryManagement';
import MonthlyLaborCost from './pages/MonthlyLaborCost';
import SalarySlips from './pages/SalarySlips';
import AssetManagementV2 from './pages/AssetManagementV2';
import AssetTypesManagement from './pages/AssetTypesManagement';
import AssetRelationsManagement from './pages/AssetRelationsManagement';
import EmployeeAssetRecords from './pages/EmployeeAssetRecords';
import PerformanceManagement from './pages/PerformanceManagement';
import Expenses from './pages/Expenses';
import Documents from './pages/Documents';
import './App.css';

const { Header, Sider, Content } = Layout;



// 主应用布局
const AppLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasPermission } = useAuth();
  const [openKeys, setOpenKeys] = React.useState(['/crm']);

  // 根据当前路径自动管理菜单展开状态
  React.useEffect(() => {
    const currentPath = location.pathname;
    const newOpenKeys = [];
    
    if (currentPath.startsWith('/crm')) {
      newOpenKeys.push('/crm');
    }
    if (currentPath.startsWith('/interviews') || currentPath.startsWith('/onboarding')) {
      newOpenKeys.push('/onboarding');
    }
    if (currentPath.startsWith('/employees')) {
      newOpenKeys.push('/employees');
    }
    if (currentPath.startsWith('/expenses')) {
      newOpenKeys.push('/expenses');
    }
    if (currentPath.startsWith('/users') || currentPath.startsWith('/role-permissions') || 
        currentPath.startsWith('/settings') || currentPath.startsWith('/system-update')) {
      newOpenKeys.push('/system-settings');
    }
    
    // 只有当newOpenKeys与当前openKeys不同时才更新，避免导航点击时自动折叠
    if (JSON.stringify(newOpenKeys) !== JSON.stringify(openKeys)) {
      setOpenKeys(newOpenKeys);
    }
  }, [location.pathname]); // 移除openKeys依赖，避免无限循环

  const menuItems = [
    {
      key: '/',
      icon: <DashboardOutlined />,
      label: '仪表盘',
    },
            {
          key: '/crm',
          icon: <UserOutlined />,
          label: '人才筛选',
          hidden: !hasPermission('lead.view'),
          children: [
            {
              key: '/crm/public',
              label: '简历公海',
            },
            {
              key: '/crm/my',
              label: '我的简历',
            },
            {
              key: '/crm/followup',
              label: '筛选记录',
            },
          ]
        },
    {
      key: '/onboarding',
      icon: <FileTextOutlined />,
      label: '入职流程',
      hidden: !hasPermission('onboarding.view') && !hasPermission('interview.view'),
      children: [
        {
          key: '/interviews',
          label: '面试管理',
          hidden: !hasPermission('interview.view')
        },
        {
          key: '/onboarding/list',
          label: '入职管理',
          hidden: !hasPermission('onboarding.view')
        },
      ].filter(item => !item.hidden)
    },
    {
      key: '/employees',
      icon: <TeamOutlined />,
      label: '员工管理',
      hidden: !hasPermission('employee.view'),
      children: [
        {
          key: '/employees/list',
          label: '员工列表',
        },
        {
          key: '/employees/tasks',
          label: '任务列表',
        },
        {
          key: '/employees/resignations',
          label: '离职工单',
        },
        {
              key: '/employees/salary',
              label: '薪资管理',
              hidden: !hasPermission('employee.view')
            },
            {
              key: '/employees/labor-cost',
              label: '用工成本',
              hidden: !hasPermission('employee.view')
            },
            {
              key: '/employees/salary-slips',
              label: '工资单',
              hidden: !hasPermission('employee.view')
            },
      ].filter(item => !item.hidden)
    },
    {
      key: '/expenses',
      icon: <WalletOutlined />,
      label: '记账管理',
      hidden: !hasPermission('expense.view'),
      children: [
        {
          key: '/expenses/list',
          label: '记账管理',
        },
      ].filter(item => !item.hidden)
    },
    {
      key: '/documents',
      icon: <FolderOpenOutlined />,
      label: '文档管理',
      hidden: false,
    },
    {
      key: '/system-settings',
      icon: <SettingOutlined />,
      label: '系统设置',
      hidden: !hasPermission('settings.view') && !hasPermission('user.view') && !hasPermission('permission.view'),
      children: [
        {
          key: '/users',
          label: '用户管理',
          hidden: !hasPermission('user.view')
        },
        {
          key: '/role-permissions',
          label: '权限管理',
          hidden: !hasPermission('permission.view')
        },
        {
          key: '/settings',
          label: '系统配置',
          hidden: !hasPermission('settings.view')
        },
        {
          key: '/system-update',
          label: '系统更新',
          hidden: !hasPermission('settings.view')
        },
      ].filter(item => !item.hidden)
    },
  ].filter(item => !item.hidden);

  const handleMenuClick = ({ key }) => {
    navigate(key);
    // 菜单展开状态由useEffect自动管理，这里不需要额外处理
  };

  const handleOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // 测试页面和移动端页面不需要登录检查
  if (!user && location.pathname !== '/test-login' && location.pathname !== '/mobile/call' && location.pathname !== '/mobile/expenses' && location.pathname !== '/mobile/categories' && location.pathname !== '/mobile/learning') {
    return <Login />;
  }

  // 移动端页面使用特殊布局
  if (location.pathname === '/mobile/call' || location.pathname === '/mobile/expenses' || location.pathname === '/mobile/categories' || location.pathname === '/mobile/learning') {
    return (
      <Routes>
        <Route path="/mobile/call" element={<CallPage />} />
        <Route path="/mobile/expenses" element={<ExpensesPage />} />
        <Route path="/mobile/categories" element={<CategoriesPage />} />
        <Route path="/mobile/learning" element={<LearningPage />} />
      </Routes>
    );
  }

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider width={200} theme="dark">
        <div className="logo">
          <TeamOutlined className="logo-icon" />
          HR系统
        </div>
        <Menu
          mode="inline"
          theme="dark"
          selectedKeys={[location.pathname]}
          openKeys={openKeys}
          onOpenChange={handleOpenChange}
          items={menuItems}
          onClick={handleMenuClick}
        />
      </Sider>
      <Layout>
        <Header style={{ background: '#fff', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
          <Space>
            <span>欢迎，{user.username}</span>
            <Tag color="blue">{user.role === 'super_admin' ? '超级管理员' : user.role === 'hr_manager' ? '人事主管' : '人事助理'}</Tag>
            <Button icon={<LogoutOutlined />} onClick={handleLogout}>
              退出
            </Button>
          </Space>
        </Header>
        <Content>
          <Routes>
            <Route path="/test-login" element={<TestLogin />} />
            <Route path="/" element={<Dashboard />} />
            <Route path="/users" element={<Users />} />
            <Route path="/role-permissions" element={<RolePermissions />} />
            <Route path="/crm/public" element={<PublicLeads />} />
            <Route path="/crm/my" element={<MyLeads />} />
            <Route path="/crm/followup" element={<FollowUpRecords />} />
            <Route path="/crm" element={<Navigate to="/crm/public" replace />} />
            <Route path="/interviews" element={<Interviews />} />
            <Route path="/employees" element={<Navigate to="/employees/list" replace />} />
            <Route path="/employees/list" element={<Employees />} />
            <Route path="/employees/tasks" element={<EmployeesTasksAll />} />
            <Route path="/employees/resignations" element={<Resignations />} />
            <Route path="/employees/salary" element={<SalaryManagement />} />
            <Route path="/employees/labor-cost" element={<MonthlyLaborCost />} />
            <Route path="/employees/salary-slips" element={<SalarySlips />} />
            <Route path="/resignations" element={<Navigate to="/employees/resignations" replace />} />
            <Route path="/onboarding" element={<Navigate to="/onboarding/list" replace />} />
            <Route path="/onboarding/list" element={<Onboarding />} />
            <Route path="/assets" element={<Navigate to="/assets/list" replace />} />
            <Route path="/assets/list" element={<AssetManagementV2 />} />
            <Route path="/assets/employee-records" element={<EmployeeAssetRecords />} />
            <Route path="/assets/types" element={<AssetTypesManagement />} />
            <Route path="/assets/relations" element={<AssetRelationsManagement />} />
            <Route path="/performance" element={<Navigate to="/performance/list" replace />} />
            <Route path="/performance/list" element={<PerformanceManagement />} />
            <Route path="/expenses" element={<Navigate to="/expenses/list" replace />} />
            <Route path="/expenses/list" element={<Expenses />} />
            <Route path="/learning/courses" element={<CourseManagement />} />
            <Route path="/learning/videos" element={<VideoManagement />} />
            <Route path="/learning/records" element={<LearningRecords />} />
            <Route path="/learning" element={<Navigate to="/learning/courses" replace />} />
            <Route path="/documents" element={<Documents />} />
            <Route path="/settings" element={<Settings />} />
            <Route path="/system-update" element={<SystemUpdate />} />
          </Routes>
        </Content>
      </Layout>
    </Layout>
  );
};

function App() {
  return (
  <AuthProvider>
      <Router>
        <AppLayout />
      </Router>
  </AuthProvider>
);
}

export default App; 